import { useCallback, useEffect, useRef, useState } from "react";
import type { CaseView, PackView } from "../api";
import { api } from "../api";
import { SectionLabel } from "./chrome";

/* ═══════════════════════════════════════════════════════════════════════
   The declaration.

   The moment the whole intake exists to reach. Whoever reviews this is
   required to hold a signed declaration BEFORE they act on it, so this is
   a legal document rather than a confirmation dialog — and the person
   signing is entitled to understand what they're agreeing to.

   Which is why the statement is shown in full rather than summarised
   behind a link, why separate obligations get separate ticks instead of
   one bundled "I agree", and why nothing here is pre-ticked.
   ═══════════════════════════════════════════════════════════════════════ */

function SignaturePad({
  onChange,
  disabled,
}: {
  onChange(dataUrl: string): void;
  disabled: boolean;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const marked = useRef(false);
  const [empty, setEmpty] = useState(true);

  /**
   * Sized to its own box at device resolution. A canvas left at its CSS
   * size renders a signature that looks like a fax on any modern phone.
   */
  const fit = useCallback(() => {
    const el = canvas.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    el.width = Math.round(rect.width * dpr);
    el.height = Math.round(rect.height * dpr);
    const ctx = el.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1a1a18";
  }, []);

  useEffect(() => {
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [fit]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.preventDefault();
    // Capture, so a stroke that leaves the box doesn't end mid-signature.
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = canvas.current?.getContext("2d");
    const { x, y } = point(e);
    ctx?.beginPath();
    ctx?.moveTo(x, y);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const ctx = canvas.current?.getContext("2d");
    const { x, y } = point(e);
    ctx?.lineTo(x, y);
    ctx?.stroke();
    if (!marked.current) {
      marked.current = true;
      setEmpty(false);
    }
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (marked.current && canvas.current) onChange(canvas.current.toDataURL("image/png"));
  };

  const clear = () => {
    const el = canvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx) return;
    ctx.clearRect(0, 0, el.width, el.height);
    marked.current = false;
    setEmpty(true);
    onChange("");
  };

  return (
    <div>
      <canvas
        ref={canvas}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        style={{
          width: "100%",
          height: 120,
          background: "#fff",
          border: "1px solid var(--line-2)",
          borderRadius: "var(--radius)",
          // Without this a finger drag scrolls the page instead of drawing.
          touchAction: "none",
          cursor: disabled ? "not-allowed" : "crosshair",
          opacity: disabled ? 0.5 : 1,
        }}
      />
      <div className="flex items-center justify-between mt-1.5">
        <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
          {empty ? "Sign above with your finger or mouse" : "Looks good"}
        </span>
        <button
          type="button"
          onClick={clear}
          disabled={disabled || empty}
          style={{
            fontSize: 12,
            color: "var(--ink-3)",
            background: "none",
            border: "none",
            cursor: "pointer",
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          Clear and sign again
        </button>
      </div>
    </div>
  );
}

export function Declaration({
  kase,
  pack,
  onSigned,
}: {
  kase: CaseView;
  pack: PackView;
  onSigned(c: CaseView): void;
}) {
  const [agreed, setAgreed] = useState(false);
  const [additional, setAdditional] = useState<Record<string, boolean>>({});
  const [signedName, setSignedName] = useState(kase.fields.name ?? "");
  const [signature, setSignature] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (kase.consent) {
    return (
      <div className="card mt-3 px-4 py-3">
        <SectionLabel>Signed</SectionLabel>
        <p style={{ fontSize: 13, marginTop: 4 }}>
          Signed by <b style={{ fontWeight: 600 }}>{kase.consent.signedName}</b> on{" "}
          {new Date(kase.consent.signedAt).toLocaleString("en-AU", {
            dateStyle: "medium",
            timeStyle: "short",
          })}
          .
        </p>
        <p style={{ fontSize: 12.5, color: "var(--ink-2)", marginTop: 6 }}>
          Your {pack.copy.reviewerNoun} has everything they need. They'll be in touch if anything
          needs clarifying.
        </p>
      </div>
    );
  }

  if (!kase.canSign) {
    return (
      <div className="card mt-3 px-4 py-3">
        <SectionLabel>Declaration</SectionLabel>
        <p style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 4 }}>
          You'll sign here once you and crAIg have been through everything. Keep chatting — he'll
          let you know when it's ready.
        </p>
      </div>
    );
  }

  const allTicked =
    agreed && pack.declaration.additional.every((a) => additional[a.id]);
  const ready = allTicked && signedName.trim().length > 1 && signature.startsWith("data:image/");

  async function sign() {
    setSaving(true);
    setError(null);
    try {
      const { case: updated } = await api.sign(kase.id, {
        signedName: signedName.trim(),
        signatureImage: signature,
        additional,
      });
      onSigned(updated);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card mt-3 px-4 py-4">
      <SectionLabel>Declaration</SectionLabel>

      <p style={{ fontSize: 12.5, color: "var(--ink-2)", margin: "6px 0 12px" }}>
        Your {pack.copy.reviewerNoun} is required to hold this before they can act. Read it, then
        sign below. Nothing is sent until you do.
      </p>

      <div
        style={{
          fontSize: 13,
          lineHeight: 1.5,
          background: "var(--surface-2)",
          border: "1px solid var(--line)",
          borderRadius: "var(--radius)",
          padding: "0.7rem 0.8rem",
        }}
      >
        {pack.declaration.statement}
      </div>

      <label className="flex items-start gap-2.5 mt-3" style={{ fontSize: 13, cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          style={{ marginTop: 3, accentColor: "var(--accent)", width: 16, height: 16 }}
        />
        <span>I have read the above and it is true and correct.</span>
      </label>

      {/* Separate obligations get separate ticks. Bundling them into one
          "I agree" would be quicker and would not be consent. */}
      {pack.declaration.additional.map((a) => (
        <div key={a.id} className="mt-3">
          <div
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              background: "var(--surface-2)",
              border: "1px solid var(--line)",
              borderRadius: "var(--radius)",
              padding: "0.7rem 0.8rem",
            }}
          >
            {a.text}
          </div>
          <label
            className="flex items-start gap-2.5 mt-2"
            style={{ fontSize: 13, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={Boolean(additional[a.id])}
              onChange={(e) => setAdditional((v) => ({ ...v, [a.id]: e.target.checked }))}
              style={{ marginTop: 3, accentColor: "var(--accent)", width: 16, height: 16 }}
            />
            <span>{a.label}</span>
          </label>
        </div>
      ))}

      <div className="mt-4">
        <label
          htmlFor="signed-name"
          className="block mb-1"
          style={{ fontSize: 12, fontWeight: 550, color: "var(--ink-2)" }}
        >
          Full name, as it appears on your ID
        </label>
        <input
          id="signed-name"
          value={signedName}
          onChange={(e) => setSignedName(e.target.value)}
          className="field"
          autoComplete="name"
        />
      </div>

      <div className="mt-3">
        <SignaturePad onChange={setSignature} disabled={!allTicked} />
      </div>

      {error && (
        <p style={{ fontSize: 12.5, color: "var(--danger)", marginTop: 8 }}>{error}</p>
      )}

      <button
        type="button"
        onClick={sign}
        disabled={!ready || saving}
        className="btn btn-primary mt-3 w-full"
      >
        {saving ? "Sending…" : "Sign and send to my " + pack.copy.reviewerNoun}
      </button>

      {!ready && (
        <p style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6, textAlign: "center" }}>
          {!allTicked
            ? "Tick the boxes above to sign"
            : signedName.trim().length <= 1
              ? "Add your full name"
              : "Draw your signature to finish"}
        </p>
      )}

      <p style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 10 }}>
        An electronic signature is accepted here. Your {pack.copy.reviewerNoun} still reviews
        everything and may come back to you before anything is filed.
      </p>
    </div>
  );
}
